export type GalleryCategory = "worship" | "community" | "youth" | "baptisms";

export interface InstagramMediaItem {
    id: string;
    caption: string;
    mediaType: "IMAGE" | "CAROUSEL_ALBUM" | "VIDEO";
    mediaUrl: string;
    permalink: string;
    timestamp: string;
    category: GalleryCategory;
    categories?: GalleryCategory[];
    altText?: string;
    likesCount?: number;
}

export interface InstagramResponse {
    items: InstagramMediaItem[];
    source: "api" | "fallback";
    handle: string;
    profileUrl: string;
}

export const INSTAGRAM_HANDLE = "icocauckland";
export const INSTAGRAM_PROFILE_URL = "https://www.instagram.com/icocauckland/";

export const FALLBACK_GALLERY_ITEMS: InstagramMediaItem[] = [
    {
        id: "insta_1",
        caption: "Rejoicing in heaven and on earth! Celebrating a new brother in Christ baptized into Jesus at the beach. Welcome to the kingdom family!",
        mediaType: "IMAGE",
        mediaUrl: "/images/gallery/photo1.jpg",
        permalink: INSTAGRAM_PROFILE_URL,
        timestamp: "2026-03-01T11:00:00Z",
        category: "baptisms",
        categories: ["baptisms", "community"],
        altText: "Brothers smiling together at the beach following a baptism in Auckland",
        likesCount: 94,
    },
    {
        id: "insta_2",
        caption: "Acoustic worship and praise night. Singing with hearts full of gratitude and praising God together as one church whanau.",
        mediaType: "IMAGE",
        mediaUrl: "/images/gallery/photo2.jpg",
        permalink: INSTAGRAM_PROFILE_URL,
        timestamp: "2026-02-24T19:30:00Z",
        category: "worship",
        categories: ["worship", "community"],
        altText: "Church members singing together with acoustic guitar during evening worship",
        likesCount: 78,
    },
    {
        id: "insta_3",
        caption: "Campus and youth sisters gathered by the water with Rangitoto in the background. Celebrating baptism, sisterhood, and faith in Jesus.",
        mediaType: "IMAGE",
        mediaUrl: "/images/gallery/photo3.jpg",
        permalink: INSTAGRAM_PROFILE_URL,
        timestamp: "2026-02-16T14:15:00Z",
        category: "youth",
        categories: ["youth", "baptisms", "community"],
        altText: "Young women smiling together on the beach celebrating a baptism by the Auckland water",
        likesCount: 105,
    },
    {
        id: "insta_4",
        caption: "Sunday preaching and biblical encouragement from the podium. Walking together in faith, discipleship, and compassionate ministry.",
        mediaType: "IMAGE",
        mediaUrl: "/images/gallery/photo4.jpg",
        permalink: INSTAGRAM_PROFILE_URL,
        timestamp: "2026-02-08T10:30:00Z",
        category: "worship",
        categories: ["worship"],
        altText: "Speaker sharing an encouraging biblical lesson at the pulpit during Sunday church service",
        likesCount: 62,
    },
    {
        id: "insta_5",
        caption: "Serving our Auckland community with love and care! Tree planting and environmental cleanup day across the beautiful hills.",
        mediaType: "IMAGE",
        mediaUrl: "/images/gallery/photo5.jpg",
        permalink: INSTAGRAM_PROFILE_URL,
        timestamp: "2026-01-28T13:00:00Z",
        category: "community",
        categories: ["community"],
        altText: "Church volunteers in high-visibility vests celebrating together after tree planting outdoors",
        likesCount: 89,
    },
    {
        id: "insta_6",
        caption: "Joyous wedding celebration in God's family! Celebrating covenant love and cheering for our newly married brother and sister.",
        mediaType: "IMAGE",
        mediaUrl: "/images/gallery/photo6.jpg",
        permalink: INSTAGRAM_PROFILE_URL,
        timestamp: "2026-01-18T15:30:00Z",
        category: "community",
        categories: ["community"],
        altText: "Bride and groom walking together joyfully under floral arch with church family clapping",
        likesCount: 134,
    },
];

export function getFallbackInstagramItems(): InstagramMediaItem[] {
    return FALLBACK_GALLERY_ITEMS;
}

export function inferCategories(caption?: string): GalleryCategory[] {
    if (!caption) return ["community"];
    const text = caption.toLowerCase();
    const categories = new Set<GalleryCategory>();

    // 1. Baptisms (and whole church community celebration)
    if (
        text.includes("welcome to the kingdom") ||
        text.includes("welcome to the family") ||
        text.includes("born again") ||
        text.includes("baptis")
    ) {
        categories.add("baptisms");
        categories.add("community");
    }

    // 2. Youth & Campus / Young Professionals / Teens
    if (
        text.includes("youth") ||
        text.includes("campus") ||
        text.includes("teen") ||
        text.includes("pros retreat") ||
        text.includes("young pros")
    ) {
        categories.add("youth");
    }

    // Strip invite footer before worship check so general invitations don't false positive
    const contentText = text.replace(/come join our sunday services/g, "");

    // 3. Sunday Worship & Services
    if (
        contentText.includes("worship") ||
        contentText.includes("sunday") ||
        contentText.includes("service") ||
        contentText.includes("preaching") ||
        contentText.includes("sermon") ||
        contentText.includes("praise") ||
        contentText.includes("communion") ||
        contentText.includes("acoustic")
    ) {
        categories.add("worship");
    }

    // 4. Community events & ministries
    if (
        text.includes("women’s night") ||
        text.includes("womens night") ||
        text.includes("women's night") ||
        text.includes("marrieds") ||
        text.includes("showtime") ||
        text.includes("spacorps") ||
        text.includes("fellowship") ||
        text.includes("retreat") ||
        text.includes("weekend") ||
        text.includes("family")
    ) {
        categories.add("community");
    }

    // Fallback: If no category was matched, default to community
    if (categories.size === 0) {
        categories.add("community");
    }

    return Array.from(categories);
}

export function inferCategory(caption?: string): GalleryCategory {
    const cats = inferCategories(caption);
    // Prefer specific category over generic community if multiple match
    if (cats.includes("baptisms")) return "baptisms";
    if (cats.includes("youth")) return "youth";
    if (cats.includes("worship")) return "worship";
    return cats[0] || "community";
}

export function filterMediaByCategory(items: InstagramMediaItem[], category?: string): InstagramMediaItem[] {
    if (!category || category === "all") {
        return items;
    }
    return items.filter((item) => {
        if (Array.isArray(item.categories) && item.categories.length > 0) {
            return item.categories.includes(category as GalleryCategory);
        }
        return item.category === category;
    });
}

export function formatMediaDate(timestamp: string): string {
    try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
            return "";
        }
        return date.toLocaleDateString("en-NZ", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return "";
    }
}

export async function fetchInstagramPhotos(): Promise<InstagramResponse> {
    try {
        const res = await fetch("/api/instagram");
        if (!res.ok) {
            return {
                items: getFallbackInstagramItems(),
                source: "fallback",
                handle: INSTAGRAM_HANDLE,
                profileUrl: INSTAGRAM_PROFILE_URL,
            };
        }
        const data = (await res.json()) as { items?: InstagramMediaItem[]; handle?: string; profileUrl?: string };
        if (!data.items || data.items.length === 0) {
            return {
                items: getFallbackInstagramItems(),
                source: "fallback",
                handle: INSTAGRAM_HANDLE,
                profileUrl: INSTAGRAM_PROFILE_URL,
            };
        }
        const items: InstagramMediaItem[] = data.items.map((item) => {
            const categories = inferCategories(item.caption);
            return {
                ...item,
                categories,
                category: inferCategory(item.caption),
            };
        });

        return {
            items,
            source: "api",
            handle: data.handle || INSTAGRAM_HANDLE,
            profileUrl: data.profileUrl || INSTAGRAM_PROFILE_URL,
        };
    } catch {
        return {
            items: getFallbackInstagramItems(),
            source: "fallback",
            handle: INSTAGRAM_HANDLE,
            profileUrl: INSTAGRAM_PROFILE_URL,
        };
    }
}
